<template>
  <main-container title="データ収集">
    <div slot="main-content">
      <form-container class="data-collection-container">
        <form-item-container label="収集データ選択">
          <field-container>
            <div class="form-standard data-collection-select" @click="visibleDataCollectionSelect = true">{{
              getDataCollectionText(selectedDataCollection) }}
            </div>
            <data-collection-select v-if="visibleDataCollectionSelect"
                                    :selected-data-collection-for-init="selectedDataCollection"
                                    @close="visibleDataCollectionSelect = false"
                                    @select="onSelectDataCollection">
            </data-collection-select>
          </field-container>
        </form-item-container>

        <template v-if="selectedDataCollection">
          <form-item-container v-if="hasAvailableFilter()" label="対象設定">
            <field-container v-if="isAvailableFilter('prefs')" :class="{ 'has-error': prefValidateError }"
                             label="都道府県を選択">
              <p slot="field-item-label">都道府県を選択<br><span class="annotation">※都道府県を複数選択すると、データ収集に時間がかかる場合があります</span>
              </p>
              <div class="collect-input">
                <div class="form-standard prefecture-select" @click="visiblePrefectureSelect = true">{{
                  getPrefecturesSelectText() }}
                </div>
                <span class="error-message"><strong>都道府県を選択してください</strong></span>
              </div>
              <prefecture-select v-if="visiblePrefectureSelect"
                                 :multiple-select="true"
                                 :current-prefectures="selectedPrefectures"
                                 @close="visiblePrefectureSelect = false"
                                 @select="onSelectPrefecture">
              </prefecture-select>
            </field-container>

            <field-container v-if="isAvailableFilter('pref')" :class="{ 'has-error': prefValidateError }"
                             label="都道府県を選択">
              <div class="collect-input">
                <div class="form-standard prefecture-select" @click="visiblePrefectureSelect = true">{{
                  getPrefectureSelectText() }}
                </div>
                <span class="error-message"><strong>都道府県を選択してください</strong></span>
              </div>
              <prefecture-select v-if="visiblePrefectureSelect"
                                 :current-prefectures="selectedPrefecture ? [selectedPrefecture] : []"
                                 :multiple-select="false"
                                 @close="visiblePrefectureSelect = false"
                                 @select="onSelectPrefecture">
              </prefecture-select>
            </field-container>

            <field-container v-if="isAvailableFilter('yearMonth')" label="期間">
              <div class="collect-date-picker" :class="{ 'has-error': yearMonthValidateError}">
                <el-date-picker v-model="fromYearMonth" :picker-options="yearMonthPickerOption" :clearable="false"
                                type="month" placeholder="開始年月" format="yyyy/MM" value-format="yyyyMM"></el-date-picker>
                <span style="margin: 0 16px;">～</span>
                <el-date-picker v-model="toYearMonth" :picker-options="yearMonthPickerOption" :clearable="false"
                                type="month" placeholder="終了年月" format="yyyy/MM" value-format="yyyyMM"></el-date-picker>
                <span class="error-message"><strong>正しい期間を選択してください</strong></span>
              </div>
            </field-container>

            <field-container v-if="isAvailableFilter('year')" label="期間">
              <div class="collect-date-picker" :class="{ 'has-error': yearValidateError}">
                <el-date-picker v-model="fromYear" :picker-options="yearPickerOption" :clearable="false" type="year"
                                placeholder="開始年" format="yyyy" value-format="yyyy"></el-date-picker>
                <span style="margin: 0 16px;">～</span>
                <el-date-picker v-model="toYear" :picker-options="yearPickerOption" :clearable="false" type="year"
                                placeholder="終了年" format="yyyy" value-format="yyyy"></el-date-picker>
                <span class="error-message"><strong>正しい期間を選択してください</strong></span>
              </div>
            </field-container>

            <field-container v-if="isAvailableFilter('address')" label="住所を入力">
              <field-input class="collect-input" v-model="address"></field-input>
            </field-container>

            <field-container v-if="isAvailableFilter('corporateName')"
                             :class="{ 'has-error': $validator.errors.has('企業名') }" label="企業名を入力">
              <field-input name="corporate_name" class="collect-input" validate="required" validateLabel="企業名"
                           v-model="corporateName"></field-input>
              <span class="error-message" v-if="$validator.errors.has('corporate_name')"><strong
                      v-html="$options.filters.html_format($validator.errors.first('corporate_name'))"></strong></span>
            </field-container>

          </form-item-container>
        </template>
      </form-container>

      <collect-result-dialog v-if="collectResult != null && visibleCollectResult"
                             :visible="visibleCollectResult"
                             :data-collection-id="executedDataCollectionId"
                             :download-param="executedDownloadParam"
                             :collect-result="collectResult"
                             @close="visibleCollectResult=false">
      </collect-result-dialog>

      <collect-corporation-result-dialog v-if="collectResult != null && visibleCollectCorporationResult"
                                         :visible="visibleCollectCorporationResult"
                                         :data-collection-id="executedDataCollectionId"
                                         :download-param="executedDownloadParam"
                                         :collect-result="collectResult"
                                         @close="visibleCollectCorporationResult=false">
      </collect-corporation-result-dialog>
    </div>
    <template slot="main-footer">
      <el-button :disabled="!hasSelectedDataCollection()" class="large" type="primary" @click="onClickCollect">データを収集
      </el-button>
      <el-button class="large" type="info" @click="onClickShowResult" v-show="collectResult != null">結果を表示</el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../base'
import DataCollectionSelectCmp from '../../components/data-collect/dialogs/data-collect-select.vue'
import PrefectureSelectDialogCmp from '../../components/data-collect/dialogs/prefecture-select.vue'
import CollectResultDialogCmp from '../../components/data-collect/dialogs/collect-result.vue'
import CollectCorporationResultDialogCmp from '../../components/data-collect/dialogs/collect-corporation-result.vue'
import WebApiClient from '../../api/web'
import CommonUtil from '../../utils/common'
import DateUtil from '../../utils/date'
import { DATA_COLLECTION_DEFINES } from '../../defines/data-collection'

export default {
  name: 'DataCollectView',
  mixins: [BaseView],
  components: {
    'data-collection-select': DataCollectionSelectCmp,
    'prefecture-select': PrefectureSelectDialogCmp,
    'collect-result-dialog': CollectResultDialogCmp,
    'collect-corporation-result-dialog': CollectCorporationResultDialogCmp
  },
  props: {},
  computed: {
    minYearMonth () {
      return '200001'
    },
    maxYearMonth () {
      return '201711'
    }
  },
  data () {
    return {
      maxYear: '',
      minYear: '',
      address: '',
      corporateName: '',
      fromYear: '',
      toYear: '',
      fromYearMonth: '200001',
      toYearMonth: '200001',
      selectedPrefecture: null,
      selectedPrefectures: [],
      visiblePrefectureSelect: false,
      selectedDataCollection: null,
      visibleDataCollectionSelect: false,
      collectResult: null,
      visibleCollectResult: false,
      visibleCollectCorporationResult: false,
      executedDataCollectionId: '',
      executedDownloadParam: null,
      prefValidateError: false,
      yearMonthValidateError: false,
      yearValidateError: false,
      yearMonthPickerOption: {
        disabledDate: (date) => this.isDisabledDate(date, 'yearMonth')
      },
      yearPickerOption: {
        disabledDate: (date) => this.isDisabledDate(date, 'year')
      }
    }
  },
  methods: {
    isDisabledDate (date, dateType) {
      let minDate, maxDate
      if (dateType === 'yearMonth') {
        minDate = DateUtil.parse(this.minYearMonth, 'YYYYMM')
        maxDate = DateUtil.parse(this.maxYearMonth, 'YYYYMM')
      } else if (dateType === 'year') {
        minDate = DateUtil.parse(this.minYear, 'YYYY')
        maxDate = DateUtil.parse(this.maxYear, 'YYYY')
      }
      return minDate.isAfter(date) || maxDate.isBefore(date)
    },
    getDataCollectionText (dataCollection) {
      if (CommonUtil.isEmpty(dataCollection)) {
        return 'データの種類を選択してください'
      }

      if (CommonUtil.isNotEmpty(dataCollection.categoryId)) {
        return dataCollection.dataCollectionName + ' - ' + dataCollection.categoryName
      } else {
        return dataCollection.dataCollectionName
      }
    },
    hasSelectedDataCollection () {
      return CommonUtil.isNotEmpty(this.selectedDataCollection)
    },
    hasAvailableFilter () {
      const targetDataCollection = DATA_COLLECTION_DEFINES.filter(data =>
        this.selectedDataCollection.dataCollectionId === data.dataCollectionId &&
          this.selectedDataCollection.categoryId === data.categoryId
      )[0]
      return CommonUtil.isNotEmpty(targetDataCollection.filterTypes)
    },
    isAvailableFilter (filterName) {
      const targetDataCollection = DATA_COLLECTION_DEFINES.filter(data =>
        this.selectedDataCollection.dataCollectionId === data.dataCollectionId &&
          this.selectedDataCollection.categoryId === data.categoryId
      )[0]

      return targetDataCollection.filterTypes.indexOf(filterName) !== -1
    },
    getPrefecturesSelectText () {
      const prefectureNames = this.selectedPrefectures.map(prefecture => prefecture.prefName)
      return CommonUtil.isNotEmpty(prefectureNames) ? prefectureNames.join(', ') : '都道府県を選択する'
    },
    getPrefectureSelectText () {
      const prefecture = this.selectedPrefecture
      return CommonUtil.isNotEmpty(prefecture) ? prefecture.prefName : '都道府県を選択する'
    },
    onSelectDataCollection (dataCollection) {
      const oldDataCollectionId = this.selectedDataCollection ? this.selectedDataCollection.dataCollectionId : null
      const oldCategoryId = this.selectedDataCollection ? this.selectedDataCollection.categoryId : null

      if (oldDataCollectionId !== dataCollection.dataCollectionId) {
        this.address = ''
        this.corporateName = ''
        this.fromYear = ''
        this.toYear = ''
        this.fromYearMonth = '200001'
        this.toYearMonth = '200001'

        this.selectedPrefecture = null
        this.selectedPrefectures = []
      }

      if (oldDataCollectionId !== dataCollection.dataCollectionId || oldCategoryId !== dataCollection.categoryId) {
        this.prefValidateError = false
        this.yearMonthValidateError = false
        this.yearValidateError = false

        this.selectedDataCollection = dataCollection
        if (dataCollection.dataCollectionId === '0007') {
          this.setYearRestriction(this.selectedDataCollection)
        }
      }
    },

    onSelectPrefecture (prefectures, isMultipulSelect) {
      if (isMultipulSelect) {
        this.selectedPrefectures = prefectures
        if (this.selectedPrefectures.length > 0) {
          this.prefValidateError = false
        }
      } else {
        this.selectedPrefecture = prefectures[0]
        if (CommonUtil.isNotEmpty(this.selectedPrefecture)) {
          this.prefValidateError = false
        }
      }
    },
    onClickCollect () {
      this.isValid().then(valid => {
        if (!valid) {
          return
        }

        let promise
        let param

        const dataCollectionId = this.selectedDataCollection.dataCollectionId
        this.collectResult = null

        if (dataCollectionId === '0002') {
          param = { filters: { corporateName: this.corporateName } }
          promise = WebApiClient.getDataCollectionCorporations(this, param)
        } else {
          param = this.buildDataCollectionDownloadParam()
          promise = WebApiClient.getDataCollectionDownload(this, dataCollectionId, param)
        }

        return promise.then(response => {
          this.executedDownloadParam = param
          this.executedDataCollectionId = dataCollectionId
          this.collectResult = response.data.result

          if (dataCollectionId === '0002') {
            this.visibleCollectCorporationResult = true
          } else {
            this.visibleCollectResult = true
          }
        })
      })
    },
    onClickShowResult () {
      if (this.executedDataCollectionId === '0002') {
        this.visibleCollectCorporationResult = true
      } else {
        this.visibleCollectResult = true
      }
    },
    setYearRestriction (dataCollection) {
      const categoryId = dataCollection.categoryId
      if (categoryId === 'conv_pwp_tao_trans') {
        this.minYear = '1996'
        this.maxYear = '2016'
      } else if (categoryId === 'conv_pwp_nobc_ap_trans' || categoryId === 'conv_pwp_as_nfvj_trans') {
        this.minYear = '2007'
        this.maxYear = '2016'
      }

      this.fromYear = this.minYear
      this.toYear = this.maxYear
    },
    isValid () {
      if (this.isAvailableFilter('prefs') && CommonUtil.isEmpty(this.selectedPrefectures)) {
        this.prefValidateError = true
      }
      if (this.isAvailableFilter('pref') && CommonUtil.isEmpty(this.selectedPrefecture)) {
        this.prefValidateError = true
      }

      if (this.isAvailableFilter('yearMonth')) {
        if ((CommonUtil.isEmpty(this.fromYearMonth) || CommonUtil.isEmpty(this.toYearMonth))) {
          this.yearMonthValidateError = true
        }
      }

      return new Promise((resolve) => {
        this.$validator.validateAll().then((result) => {
          resolve(CommonUtil.isNotEmpty(this.selectedDataCollection) && !this.prefValidateError &&
              !this.yearMonthValidateError && !this.yearValidateError && result === true)
        })
      })
    },
    buildDataCollectionDownloadParam () {
      let param = {
        filters: {}
      }

      // category
      const categoryId = this.selectedDataCollection.categoryId
      if (CommonUtil.isNotEmpty(categoryId)) { param.categoryId = categoryId }

      // prefs
      if (this.isAvailableFilter('prefs')) {
        param.filters.prefs = this.selectedPrefectures.map(prefecture => prefecture.prefId)
      } else if (this.isAvailableFilter('pref')) { param.filters.prefs = [this.selectedPrefecture.prefId] }

      // address
      if (this.isAvailableFilter('address')) { param.filters.address = this.address }

      // period
      if (this.isAvailableFilter('year') || this.isAvailableFilter('yearMonth')) {
        param.filters.period = {}
      }
      if (this.isAvailableFilter('year')) {
        param.filters.period = {
          fromYear: this.fromYear,
          toYear: this.toYear
        }
      }
      if (this.isAvailableFilter('yearMonth')) {
        param.filters.period = {
          fromYear: this.fromYearMonth.slice(0, 4),
          fromMonth: this.fromYearMonth.slice(-2),
          toYear: this.toYearMonth.slice(0, 4),
          toMonth: this.toYearMonth.slice(-2)
        }
      }
      return param
    }
  }
}

</script>

<style scoped>
  .data-collection-container {
    max-width: 1000px;
    padding: 24px;
    height: 100%;
  }

  .data-collection-container .annotation {
    display: block;
    font-size: 0.75rem;
    font-weight: normal;
    text-indent: -1em;
    margin: 0 0 0 1em;
    line-height: 1.5;
  }

  .data-collection-select,
  .prefecture-select {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration-line: underline;
    color: #2456d4;
    cursor: pointer;
  }

  .collect-date-picker {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .collect-date-picker >>> .el-date-editor.el-input {
    width: inherit;
  }
</style>



// WEBPACK FOOTER //
// src/views/data/collect.vue